import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./Nursing.module.css";
import {
  FaSearch,
  FaUser,
  FaClock,
  FaMars,
  FaVenus,
  FaHeartbeat,
  FaThermometerHalf,
  FaWeight,
  FaRulerVertical,
  FaSpinner,
  FaUserMd,
  FaCalendarAlt,
  FaTimes,
  FaCheckCircle,
  FaPrint,
  FaEye,
  FaFileInvoiceDollar,
  FaLock,
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

// ---------------------------------------------------------
// SKELETON LOADERS
// ---------------------------------------------------------
const SkeletonPatientCard = () => (
  <div className={classes.skeletonPatientCard}>
    <div className={classes.skeletonAvatar}></div>
    <div className={classes.skeletonInfo}>
      <div className={classes.skeletonText} style={{ width: "60%" }}></div>
      <div className={classes.skeletonText} style={{ width: "40%" }}></div>
      <div className={classes.skeletonText} style={{ width: "30%" }}></div>
    </div>
    <div className={classes.skeletonBadge}></div>
  </div>
);

const SkeletonVitalSign = () => (
  <div className={classes.skeletonVitalSign}>
    <div className={classes.skeletonVitalLabel}></div>
    <div className={classes.skeletonVitalValue}></div>
  </div>
);

// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------
const safeId = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "object") return val.id ?? null;
  return val;
};

const safeString = (val, fallback = "—") => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === "string") return val || fallback;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object") {
    return (
      val.name ||
      val.title ||
      val.label ||
      val.department_name ||
      val.value ||
      fallback
    );
  }
  return fallback;
};

const formatNaira = (amount) => {
  const num = Number(amount || 0);
  return `₦${num.toLocaleString("en-NG")}`;
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
};

const computeAge = (dob) => {
  if (!dob) return null;
  try {
    const birth = new Date(dob);
    if (isNaN(birth.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
};

const resolveAge = (patient, q) => {
  const direct = patient?.age ?? q?.age ?? q?.patient_age ?? null;

  if (direct !== null && direct !== undefined && direct !== "") {
    return direct;
  }

  const computed =
    computeAge(patient?.date_of_birth) ??
    computeAge(q?.date_of_birth) ??
    computeAge(patient?.dob) ??
    null;

  return computed ?? "—";
};

const getWaitingTime = (q) => {
  if (typeof q.waiting_minutes === "number") {
    const mins = q.waiting_minutes;
    if (mins < 0 || mins < 1) return "Just now";
    if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"}`;
    const hrs = Math.floor(mins / 60);
    return `${hrs} hr${hrs === 1 ? "" : "s"}`;
  }
  const createdAt =
    q.created_at || q.queue_date || q.consultation?.created_at;
  if (!createdAt) return "—";
  try {
    const diff = Date.now() - new Date(createdAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min${mins > 1 ? "s" : ""}`;
    const hrs = Math.floor(mins / 60);
    return `${hrs} hr${hrs > 1 ? "s" : ""}`;
  } catch {
    return "—";
  }
};

// ---------------------------------------------------------
// BMI
// ---------------------------------------------------------
const calculateBMI = (heightCm, weightKg) => {
  const h = Number(heightCm);
  const w = Number(weightKg);
  if (!h || !w || h <= 0 || w <= 0) return "";
  const m = h / 100;
  const bmi = w / (m * m);
  if (!isFinite(bmi)) return "";
  return bmi.toFixed(1);
};

// ---------------------------------------------------------
// PAID-ONLY CHECK
// ---------------------------------------------------------
const isFullyPaid = (q) => {
  const inv = q.invoice || q.consultation?.invoice || q.bill || null;

  const status = String(
    inv?.status || q.payment_status || q.invoice_status || ""
  ).toLowerCase();

  const amountPaid = Number(
    inv?.amount_paid ?? q.amount_paid ?? q.paid_amount ?? 0
  );
  const dueAmount = Number(
    inv?.due_amount ?? q.due_amount ?? q.balance ?? -1
  );

  if (
    status === "unpaid" ||
    status === "partial" ||
    status === "partially_paid" ||
    status === "pending"
  ) {
    return false;
  }

  if (
    status === "paid" ||
    status === "settled" ||
    status === "completed"
  ) {
    return true;
  }

  if (dueAmount === 0 && amountPaid > 0) return true;

  return false;
};

// ---------------------------------------------------------
// EXTRACT LIST
// ---------------------------------------------------------
const extractPatientList = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.queue)) return payload.queue;
  if (payload.queue && Array.isArray(payload.queue.data))
    return payload.queue.data;
  if (Array.isArray(payload.patients)) return payload.patients;
  if (Array.isArray(payload.consultations)) return payload.consultations;
  if (
    payload.consultations &&
    Array.isArray(payload.consultations.data)
  )
    return payload.consultations.data;

  if (Array.isArray(payload.list)) return payload.list;
  if (payload.list && Array.isArray(payload.list.data))
    return payload.list.data;

  for (const key of Object.keys(payload)) {
    const v = payload[key];
    if (Array.isArray(v)) return v;
    if (v && typeof v === "object" && Array.isArray(v.data))
      return v.data;
  }

  return [];
};

// ---------------------------------------------------------
// NORMALIZE QUEUE / ASSIGNED ENTRY
// ---------------------------------------------------------
const normalizeQueueEntry = (q) => {
  const patient = q.patient || {};
  const consultation = q.consultation || q;
  const invoice = q.invoice || q.consultation?.invoice || {};
  const doctor =
    q.doctor || q.assigned_doctor || consultation.doctor || null;

  const nurse = consultation.nurse || q.nurse || null;
  const nurseId =
    safeId(nurse?.id) ??
    consultation.nurse_id ??
    q.nurse_id ??
    null;
  const nurseName = nurse
    ? nurse.name ||
      `${nurse.first_name || ""} ${nurse.last_name || ""}`.trim() ||
      "—"
    : "—";

  const first = patient.first_name || "";
  const middle = patient.middle_name || "";
  const last = patient.last_name || "";
  const name =
    patient.name ||
    `${first} ${middle} ${last}`.replace(/\s+/g, " ").trim() ||
    "Unnamed";

  const patientNo =
    patient.patient_no ||
    patient.uhid ||
    patient.patient_number ||
    patient.mrn ||
    "—";

  const consultationId =
    (typeof consultation.id === "number" && consultation.id) ??
    q.consultation_id ??
    q.id ??
    null;

  const consultationType =
    consultation.consultation_type_name ||
    consultation.consultation_type ||
    consultation.type ||
    q.consultation_type_name ||
    q.consultation_type ||
    patient.consultation_type ||
    "";

  const appointment = q.appointment || consultation.appointment || null;

  return {
    id: consultationId,
    consultationId,
    patientId: safeId(patient.id) ?? patient.id,
    name,
    opd: patientNo,
    gender: patient.gender || "—",
    age: resolveAge(patient, q),
    phone: patient.phone || patient.phone_number || "—",
    status: consultation.status || q.status || "Waiting",
    waitingTime: getWaitingTime(q),
    department: safeString(patient.department || q.department),
    visitDate: formatDate(
      consultation.schedule_date ||
        q.schedule_date ||
        q.created_at ||
        q.queue_date
    ),
    createdAt: q.created_at || q.queue_date || consultation.created_at,
    assignedDoctor: doctor
      ? {
          id: safeId(doctor.id),
          name:
            doctor.name ||
            `${doctor.first_name || ""} ${doctor.last_name || ""}`.trim(),
          department:
            typeof doctor.department === "object"
              ? doctor.department?.name
              : doctor.department || "General",
          designation:
            typeof doctor.designation === "object"
              ? doctor.designation?.name
              : doctor.designation || "",
          avatar: doctor.profile_photo || null,
          phone: doctor.phone || "—",
        }
      : null,
    assignedNurse:
      nurseId || nurseName !== "—"
        ? {
            id: nurseId,
            name: nurseName,
          }
        : null,
    nurseId,
    nurseName,
    appointment: appointment
      ? {
          id: appointment.id ?? null,
          date: formatDate(appointment.appointment_date),
          time: appointment.appointment_time || "—",
          duration: appointment.duration_minutes ?? "—",
          type: appointment.appointment_type || "—",
          status: appointment.status || "—",
          notes: appointment.notes || "—",
        }
      : null,
    payment: {
      invoiceNo: invoice.invoice_no || "—",
      totalAmount: Number(invoice.total_amount || 0),
      amountPaid: Number(invoice.amount_paid || 0),
      dueAmount: Number(invoice.due_amount || invoice.balance || 0),
      status: invoice.status || "Paid",
      paymentMethod: invoice.payment_method || "—",
      paidAt:
        invoice.paid_at ||
        consultation.paid_at ||
        q.paid_at ||
        null,
      purpose:
        invoice.purpose ||
        consultation.consultation_type_name ||
        "consultation",
      description:
        invoice.description ||
        consultation.reason_for_visit ||
        q.reason_for_visit ||
        "—",
    },
    reasonForVisit:
      consultation.reason_for_visit || q.reason_for_visit || "—",
    priority: consultation.priority || q.priority || "—",
    consultationType: consultationType || "—",
    notes: consultation.notes || q.notes || "—",
    raw: q,
  };
};

// ---------------------------------------------------------
// RESOLVE "Recorded By"
// ---------------------------------------------------------
const resolveRecordedBy = (v, fallbackNurseName, nurseId) => {
  const rb = v.recorded_by;

  if (rb && typeof rb === "object") {
    const n =
      rb.name || `${rb.first_name || ""} ${rb.last_name || ""}`.trim();
    if (n) return n;
  }

  if (typeof rb === "string" && rb.trim()) {
    return rb.trim();
  }

  if (typeof v.recorded_by_name === "string" && v.recorded_by_name.trim()) {
    return v.recorded_by_name.trim();
  }

  if (v.nurse?.name) return v.nurse.name;
  if (v.created_by?.name) return v.created_by.name;
  if (v.user?.name) return v.user.name;

  const rbId = rb !== null && rb !== undefined ? String(rb) : null;
  if (rbId && nurseId !== null && nurseId !== undefined) {
    if (rbId === String(nurseId)) {
      return fallbackNurseName || "—";
    }
  }

  if (rbId) {
    return fallbackNurseName || "—";
  }

  return fallbackNurseName || "—";
};

// ---------------------------------------------------------
// NORMALIZE VITALS RECORD
// ---------------------------------------------------------
const normalizeVitalRecord = (v, fallbackNurseName = "—", nurseId = null) => {
  const bp =
    v.blood_pressure ??
    v.bp ??
    (v.systolic_bp && v.diastolic_bp
      ? `${v.systolic_bp}/${v.diastolic_bp}`
      : v.systolic && v.diastolic
      ? `${v.systolic}/${v.diastolic}`
      : "—");

  const rr = v.respiratory_rate ?? v.rr ?? "—";

  const recordedByName = resolveRecordedBy(
    v,
    fallbackNurseName,
    nurseId
  );

  const date =
    v.recorded_at ||
    v.created_at ||
    v.date ||
    v.updated_at ||
    null;

  const bmi = v.bmi ?? calculateBMI(v.height, v.weight) ?? "—";

  return {
    id: v.id ?? null,
    date: date ? formatDateTime(date) : "—",
    bp: bp || "—",
    pulse: v.pulse ?? "—",
    rr: rr || "—",
    temp: v.temperature ?? v.temp ?? "—",
    spo2: v.spo2 ?? "—",
    height: v.height ?? "—",
    weight: v.weight ?? "—",
    bmi: bmi || "—",
    modeOfCheck: v.mode_of_check ?? v.mode ?? "—",
    remarks: v.remarks ?? v.notes ?? "—",
    recordedBy: safeString(recordedByName, "—"),
    raw: v,
  };
};

// ---------------------------------------------------------
// PAYMENT EVIDENCE MODAL
// ---------------------------------------------------------
const PaymentEvidenceModal = ({ open, onClose, patient }) => {
  if (!open || !patient) return null;

  const p = patient.payment || {};

  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div
        className={classes.evidenceModal}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.evidenceHeader}>
          <div className={classes.evidenceHeaderLeft}>
            <div className={classes.evidenceIcon}>
              <FaFileInvoiceDollar />
            </div>
            <div>
              <h3>Payment Evidence</h3>
              <p>
                {patient.name} • {patient.opd}
              </p>
            </div>
          </div>
          <button
            className={classes.modalClose}
            onClick={onClose}
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        <div className={classes.evidenceBody}>
          <div className={classes.paidVerifiedBanner}>
            <FaCheckCircle />
            <div>
              <strong>Bill paid in full</strong>
              <p>This patient has settled their consultation bill.</p>
            </div>
          </div>

          <div className={classes.evidenceGrid}>
            <div className={classes.evidenceItem}>
              <span className={classes.evidenceLabel}>Invoice No.</span>
              <span className={classes.evidenceValue}>{p.invoiceNo}</span>
            </div>
            <div className={classes.evidenceItem}>
              <span className={classes.evidenceLabel}>Purpose</span>
              <span className={classes.evidenceValue}>
                {p.purpose || "—"}
              </span>
            </div>
            <div className={classes.evidenceItem}>
              <span className={classes.evidenceLabel}>Payment Method</span>
              <span className={classes.evidenceValue}>
                {p.paymentMethod}
              </span>
            </div>
            <div className={classes.evidenceItem}>
              <span className={classes.evidenceLabel}>Paid At</span>
              <span className={classes.evidenceValue}>
                {formatDateTime(p.paidAt)}
              </span>
            </div>
            <div className={classes.evidenceItem}>
              <span className={classes.evidenceLabel}>Status</span>
              <span
                className={`${classes.evidenceBadge} ${
                  String(p.status).toLowerCase() === "paid"
                    ? classes.paid
                    : classes.partial
                }`}
              >
                {p.status || "Paid"}
              </span>
            </div>
          </div>

          <div className={classes.amountBreakdown}>
            <div className={classes.amountRow}>
              <span>Total Invoiced</span>
              <strong>{formatNaira(p.totalAmount)}</strong>
            </div>
            <div className={classes.amountRow}>
              <span>Amount Paid</span>
              <strong className={classes.amountPaid}>
                {formatNaira(p.amountPaid)}
              </strong>
            </div>
            <div className={classes.amountRow}>
              <span>Outstanding</span>
              <strong
                className={
                  p.dueAmount > 0 ? classes.amountDue : classes.amountSettled
                }
              >
                {formatNaira(p.dueAmount)}
              </strong>
            </div>
          </div>

          {p.description && p.description !== "—" && (
            <div className={classes.evidenceNote}>
              <span className={classes.evidenceLabel}>Reason for Visit</span>
              <p>{p.description}</p>
            </div>
          )}
        </div>

        <div className={classes.evidenceFooter}>
          <button
            type="button"
            className={classes.cancelModalBtn}
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            className={classes.assignScheduleBtn}
            onClick={() => window.print()}
          >
            <FaPrint /> Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------
// ASSIGN TO DOCTOR MODAL
// ---------------------------------------------------------
const AssignDoctorModal = ({ open, onClose, patient, onAssigned }) => {
  const [doctors, setDoctors] = useState([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [searchDoctor, setSearchDoctor] = useState("");

  const [visitType, setVisitType] = useState("");
  const [appointmentDate, setAppointmentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [appointmentTime, setAppointmentTime] = useState("10:00");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchDoctors();
      setSelectedDoctor(null);
      setAppointmentDate(new Date().toISOString().split("T")[0]);
      setAppointmentTime("10:00");
      setDurationMinutes(30);
      setNotes("Patient to see doctor for review of vitals and complaints.");
      setSearchDoctor("");

      const actual =
        patient?.consultationType && patient.consultationType !== "—"
          ? patient.consultationType
          : "";
      setVisitType(actual);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patient?.consultationId, patient?.consultationType]);

  const resolveDoctorId = (d) => {
    return (
      d.user_id ??
      d.user?.id ??
      d.doctor_id ??
      d.doctor?.id ??
      d.id ??
      null
    );
  };

  const fetchDoctors = async () => {
    setIsLoadingDoctors(true);
    try {
      const companyId = parseInt(localStorage.getItem("companyId") || "1");
      const response = await axios.get(`${BASE_URL}/api/staff`, {
        params: { company_id: companyId, per_page: 500 },
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      let staffList = [];
      if (response.data?.data) {
        if (Array.isArray(response.data.data)) staffList = response.data.data;
        else if (response.data.data.data) staffList = response.data.data.data;
        else if (response.data.data.staff) staffList = response.data.data.staff;
      }

      const doctorList = staffList
        .filter((s) => {
          const type = String(s.staff_type || "").toLowerCase();
          return type.includes("doctor") || type.includes("physician");
        })
        .map((d) => {
          const first = d.first_name || "";
          const last = d.last_name || "";
          const name =
            d.name || `${first} ${last}`.trim() || d.employee_no || "Unnamed";

          const deptName =
            typeof d.department === "object"
              ? d.department?.name
              : d.department || "General";

          const avail =
            d.status?.toLowerCase() === "active" ? "Available" : "Busy";

          return {
            id: resolveDoctorId(d),
            staffId: d.id ?? null,
            userId: d.user_id ?? null,
            name,
            department: deptName,
            availability: avail,
            schedule: "Mon - Fri, 8:00 AM - 4:00 PM",
            avatar: d.profile_photo || null,
          };
        })
        .filter((doc) => doc.id !== null && doc.id !== undefined);

      setDoctors(doctorList);
    } catch (error) {
      console.error("Error fetching doctors:", error);
      setDoctors([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  const filteredDoctors = doctors.filter(
    (doc) =>
      doc.name.toLowerCase().includes(searchDoctor.toLowerCase()) ||
      doc.department.toLowerCase().includes(searchDoctor.toLowerCase())
  );

  const handleAssign = async () => {
    if (!selectedDoctor) {
      Swal.fire({
        icon: "warning",
        title: "Please select a doctor",
        confirmButtonColor: "#2F80ED",
      });
      return;
    }
    if (!appointmentDate || !appointmentTime) {
      Swal.fire({
        icon: "warning",
        title: "Appointment date and time are required",
        confirmButtonColor: "#2F80ED",
      });
      return;
    }

    const consultationId = patient?.consultationId || patient?.id;
    if (!consultationId) {
      Swal.fire({
        icon: "error",
        title: "Missing consultation",
        text: "This patient is not linked to a consultation record.",
        confirmButtonColor: "#2F80ED",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const companyId = parseInt(localStorage.getItem("companyId") || "1");

      const appointmentType = String(visitType || "").slice(0, 100) || null;

      const payload = {
        company_id: companyId,
        consultation_id: consultationId,
        doctor_id: selectedDoctor.id,
        appointment_type: appointmentType,
        appointment_date: appointmentDate,
        appointment_time: appointmentTime,
        duration_minutes:
          durationMinutes && Number.isFinite(Number(durationMinutes))
            ? Math.max(1, Math.trunc(Number(durationMinutes)))
            : null,
        notes: notes?.trim() || null,
      };

      await axios.post(
        `${BASE_URL}/api/consultation_register/assign_to_doctor`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      Swal.fire({
        icon: "success",
        title: "Assigned!",
        text: `${patient?.name || "Patient"} assigned to ${selectedDoctor.name}`,
        timer: 1600,
        showConfirmButton: false,
        timerProgressBar: true,
      });

      if (onAssigned) {
        onAssigned({
          consultationId,
          doctorId: selectedDoctor.id,
          doctorName: selectedDoctor.name,
        });
      }
      onClose();
    } catch (error) {
      console.error("Error assigning to doctor:", error);

      let msg = "Failed to assign to doctor.";

      const data = error.response?.data;

      if (data?.message && typeof data.message === "object") {
        const firstKey = Object.keys(data.message)[0];
        const firstVal = data.message[firstKey];
        if (Array.isArray(firstVal) && firstVal[0]) msg = firstVal[0];
        else if (typeof firstVal === "string") msg = firstVal;
      } else if (data?.errors) {
        const errs = data.errors;
        const firstKey = Object.keys(errs)[0];
        msg = Array.isArray(errs[firstKey]) ? errs[firstKey][0] : msg;
      } else if (typeof data?.message === "string") {
        msg = data.message;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#2F80ED",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div className={classes.assignModal} onClick={(e) => e.stopPropagation()}>
        <div className={classes.assignModalBody}>
          <div className={classes.doctorSelectionPanel}>
            <h3 className={classes.assignLabel}>
              Select Doctor <span className={classes.required}>*</span>
            </h3>

            <div className={classes.doctorSearchBox}>
              <FaSearch className={classes.doctorSearchIcon} />
              <input
                type="text"
                placeholder="Search doctor by name or department"
                value={searchDoctor}
                onChange={(e) => setSearchDoctor(e.target.value)}
              />
            </div>

            <div className={classes.doctorList}>
              {isLoadingDoctors ? (
                <div className={classes.doctorLoading}>
                  <FaSpinner className={classes.spin} />
                  <span>Loading doctors...</span>
                </div>
              ) : filteredDoctors.length === 0 ? (
                <div className={classes.doctorEmpty}>No doctors found</div>
              ) : (
                filteredDoctors.map((doc) => (
                  <div
                    key={`${doc.id}-${doc.staffId ?? ""}`}
                    className={`${classes.doctorCard} ${
                      selectedDoctor?.id === doc.id
                        ? classes.doctorCardSelected
                        : ""
                    }`}
                    onClick={() => setSelectedDoctor(doc)}
                  >
                    <div className={classes.doctorAvatar}>
                      {doc.avatar ? (
                        <img src={doc.avatar} alt={doc.name} />
                      ) : (
                        <FaUserMd />
                      )}
                    </div>
                    <div className={classes.doctorInfo}>
                      <h4>{doc.name}</h4>
                      <p>{doc.department}</p>
                    </div>
                    <div className={classes.doctorRight}>
                      <span
                        className={`${classes.availabilityBadge} ${
                          doc.availability === "Available"
                            ? classes.available
                            : classes.busy
                        }`}
                      >
                        {doc.availability}
                      </span>
                      <span className={classes.doctorSchedule}>
                        {doc.schedule}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className={classes.appointmentPanel}>
            <h3 className={classes.assignLabel}>Appointment Details</h3>

            <div className={classes.assignField}>
              <label>Visit Type</label>
              <input
                type="text"
                value={visitType || "—"}
                readOnly
                disabled
                aria-readonly="true"
                title="Auto-filled from the patient's consultation type"
                style={{
                  background: "#F3F4F6",
                  color: "#374151",
                  cursor: "not-allowed",
                }}
              />
              <span className={classes.vitalSub}>
                Auto-filled from the patient's consultation type
              </span>
            </div>

            <div className={classes.assignField}>
              <label>
                Appointment Date <span className={classes.required}>*</span>
              </label>
              <div className={classes.dateInputWrap}>
                <FaCalendarAlt className={classes.dateIcon} />
                <input
                  type="date"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                />
              </div>
            </div>

            <div className={classes.assignField}>
              <label>
                Appointment Time <span className={classes.required}>*</span>
              </label>
              <div className={classes.dateInputWrap}>
                <FaClock className={classes.dateIcon} />
                <input
                  type="time"
                  value={appointmentTime}
                  onChange={(e) => setAppointmentTime(e.target.value)}
                />
              </div>
            </div>

            <div className={classes.assignField}>
              <label>Duration (minutes)</label>
              <input
                type="number"
                min="1"
                step="1"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                placeholder="e.g. 30"
              />
            </div>

            <div className={classes.assignField}>
              <label>Notes (Optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                rows="6"
                placeholder="Add any notes for the doctor..."
              />
              <span className={classes.charCount}>{notes.length}/500</span>
            </div>
          </div>
        </div>

        <div className={classes.assignModalFooter}>
          <button
            type="button"
            className={classes.cancelModalBtn}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={classes.assignScheduleBtn}
            onClick={handleAssign}
            disabled={isSubmitting || !selectedDoctor}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className={classes.spin} /> Assigning...
              </>
            ) : (
              <>
                <FaCalendarAlt /> Assign &amp; Schedule
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------
// MAIN
// ---------------------------------------------------------
const Nursing = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [allPatients, setAllPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState("queue");

  const [vitalSigns, setVitalSigns] = useState({
    bloodPressure: "",
    pulse: "",
    temperature: "",
    spo2: "",
    height: "",
    weight: "",
    bmi: "",
    respiratoryRate: "",
    modeOfCheck: "Manual",
    remarks: "",
  });

  const [latestVitals, setLatestVitals] = useState([]);
  const [isLoadingVitals, setIsLoadingVitals] = useState(false);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [isSavingVitals, setIsSavingVitals] = useState(false);

  const isAssignedView = activeTab === "assigned";

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const companyId = parseInt(localStorage.getItem("companyId") || "1");
      const params = { company_id: companyId, per_page: 100 };

      if (debouncedSearch?.trim()) params.search = debouncedSearch.trim();

      const endpoint =
        activeTab === "assigned"
          ? `${BASE_URL}/api/consultation_register/assigned_to_doctor`
          : `${BASE_URL}/api/consultation_register/queue`;

      const response = await axios.get(endpoint, {
        params,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      const payload = response.data?.data || response.data || {};
      const list = extractPatientList(payload);

      const filtered =
        activeTab === "assigned" ? list : list.filter(isFullyPaid);

      const normalized = filtered.map(normalizeQueueEntry);
      setAllPatients(normalized);

      setSelectedPatient((prev) => {
        if (prev) {
          const stillThere = normalized.find((p) => p.id === prev.id);
          if (stillThere) return stillThere;
        }
        return normalized[0] || null;
      });
    } catch (err) {
      console.error("Error fetching queue:", err);

      let msg = "Failed to load patients.";

      if (err.response?.status === 500) {
        const raw = err.response?.data?.message || "";
        if (raw.includes("undefined method")) {
          msg =
            "The patient queue is temporarily unavailable. Please contact your administrator.";
        } else {
          msg = "The server returned an error loading the queue.";
        }
      } else if (err.response?.data?.message) {
        const m = err.response.data.message;
        if (typeof m === "string") msg = m;
      } else if (err.request) {
        msg = "Unable to connect to server.";
      }

      setError(msg);
      setAllPatients([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, activeTab]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const patients = allPatients;

  const fetchVitalsHistory = useCallback(async (patient) => {
    const patientId = patient?.patientId || patient?.id;
    if (!patientId) {
      setLatestVitals([]);
      return;
    }

    const companyId = parseInt(localStorage.getItem("companyId") || "1");
    const fallbackNurseName =
      patient?.nurseName || patient?.assignedNurse?.name || "—";
    const nurseId = patient?.nurseId ?? patient?.assignedNurse?.id ?? null;

    setIsLoadingVitals(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/nursing/vitals_history`,
        {
          params: {
            company_id: companyId,
            patient_id: patientId,
          },
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      const payload = response.data?.data || response.data || {};
      let list = [];

      if (Array.isArray(payload)) list = payload;
      else if (Array.isArray(payload.data)) list = payload.data;
      else if (Array.isArray(payload.vitals)) list = payload.vitals;
      else if (payload.vitals && Array.isArray(payload.vitals.data))
        list = payload.vitals.data;
      else if (Array.isArray(payload.history)) list = payload.history;
      else if (payload.history && Array.isArray(payload.history.data))
        list = payload.history.data;
      else if (Array.isArray(payload.records)) list = payload.records;

      const normalized = list.map((v) =>
        normalizeVitalRecord(v, fallbackNurseName, nurseId)
      );
      setLatestVitals(normalized);
    } catch (err) {
      console.error("Error fetching vitals history:", err);
      setLatestVitals([]);
    } finally {
      setIsLoadingVitals(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchVitalsHistory(selectedPatient);
    } else {
      setLatestVitals([]);
    }
  }, [selectedPatient, fetchVitalsHistory]);

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setVitalSigns({
      bloodPressure: "",
      pulse: "",
      temperature: "",
      spo2: "",
      height: "",
      weight: "",
      bmi: "",
      respiratoryRate: "",
      modeOfCheck: "Manual",
      remarks: "",
    });
  };

  const handleVitalChange = (e) => {
    const { name, value } = e.target;

    setVitalSigns((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "height" || name === "weight") {
        const bmi = calculateBMI(
          name === "height" ? value : next.height,
          name === "weight" ? value : next.weight
        );
        next.bmi = bmi;
      }

      return next;
    });
  };

  const handleSaveVitals = async () => {
    if (!selectedPatient) return;

    const companyId = parseInt(localStorage.getItem("companyId") || "1");
    const consultationId =
      selectedPatient.consultationId || selectedPatient.id;

    if (!consultationId) {
      Swal.fire({
        icon: "error",
        title: "Missing consultation",
        text: "This patient is not linked to a consultation record.",
        confirmButtonColor: "#2F80ED",
      });
      return;
    }

    const errors = [];

    const bp = vitalSigns.bloodPressure?.trim() || null;
    if (bp && !/^\d{2,3}\/\d{2,3}$/.test(bp)) {
      errors.push("Blood pressure must match the format 120/80.");
    }

    const toIntOrNull = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? Math.trunc(n) : null;
    };
    const toFloatOrNull = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const pulse = toIntOrNull(vitalSigns.pulse);
    if (pulse !== null && (pulse < 20 || pulse > 250)) {
      errors.push("Pulse must be between 20 and 250 bpm.");
    }

    const rr = toIntOrNull(vitalSigns.respiratoryRate);
    if (rr !== null && (rr < 5 || rr > 80)) {
      errors.push("Respiratory rate must be between 5 and 80.");
    }

    const temp = toFloatOrNull(vitalSigns.temperature);
    if (temp !== null && (temp < 30 || temp > 115)) {
      errors.push("Temperature must be between 30 and 115 °F.");
    }

    const spo2 = toFloatOrNull(vitalSigns.spo2);
    if (spo2 !== null && (spo2 < 0 || spo2 > 100)) {
      errors.push("SpO₂ must be between 0 and 100.");
    }

    const height = toFloatOrNull(vitalSigns.height);
    if (height !== null && (height < 30 || height > 260)) {
      errors.push("Height must be between 30 and 260 cm.");
    }

    const weight = toFloatOrNull(vitalSigns.weight);
    if (weight !== null && (weight < 1 || weight > 500)) {
      errors.push("Weight must be between 1 and 500 kg.");
    }

    const modeOfCheck = vitalSigns.modeOfCheck?.trim() || null;
    if (modeOfCheck && modeOfCheck.length > 50) {
      errors.push("Mode of check must be 50 characters or fewer.");
    }

    const remarks = vitalSigns.remarks?.trim() || null;
    if (remarks && remarks.length > 1000) {
      errors.push("Remarks must be 1000 characters or fewer.");
    }

    if (errors.length) {
      Swal.fire({
        icon: "warning",
        title: "Please check your entries",
        html: `<ul style="text-align:left;margin:0;padding-left:18px;">${errors
          .map((e) => `<li>${e}</li>`)
          .join("")}</ul>`,
        confirmButtonColor: "#2F80ED",
      });
      return;
    }

    const payload = {
      company_id: companyId,
      consultation_id: consultationId,
      blood_pressure: bp,
      pulse: pulse,
      respiratory_rate: rr,
      temperature: temp,
      spo2: spo2,
      height: height,
      weight: weight,
      mode_of_check: modeOfCheck,
      remarks: remarks,
    };

    setIsSavingVitals(true);
    try {
      await axios.post(`${BASE_URL}/api/nursing/save_vitals`, payload, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      Swal.fire({
        icon: "success",
        title: "Vitals Saved",
        text: `Vitals recorded for ${selectedPatient.name}.`,
        timer: 1500,
        showConfirmButton: false,
        timerProgressBar: true,
      });

      setVitalSigns({
        bloodPressure: "",
        pulse: "",
        temperature: "",
        spo2: "",
        height: "",
        weight: "",
        bmi: "",
        respiratoryRate: "",
        modeOfCheck: "Manual",
        remarks: "",
      });

      fetchVitalsHistory(selectedPatient);
      fetchQueue();
    } catch (err) {
      console.error("Error saving vitals:", err);

      let msg = "Failed to save vitals.";

      const data = err.response?.data;
      if (data?.message && typeof data.message === "object") {
        const firstKey = Object.keys(data.message)[0];
        const firstVal = data.message[firstKey];
        if (Array.isArray(firstVal) && firstVal[0]) msg = firstVal[0];
        else if (typeof firstVal === "string") msg = firstVal;
      } else if (data?.errors) {
        const errs = data.errors;
        const firstKey = Object.keys(errs)[0];
        msg = Array.isArray(errs[firstKey]) ? errs[firstKey][0] : msg;
      } else if (typeof data?.message === "string") {
        msg = data.message;
      } else if (err.request) {
        msg = "Unable to connect to server.";
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#2F80ED",
      });
    } finally {
      setIsSavingVitals(false);
    }
  };

  const handleAssigned = () => {
    setActiveTab("assigned");
  };

  const handlePrintVitals = () => {
    if (!selectedPatient) return;

    const rows = latestVitals
      .map(
        (v) => `
        <tr>
          <td>${v.date}</td>
          <td>${v.bp}</td>
          <td>${v.pulse}</td>
          <td>${v.rr}</td>
          <td>${v.temp}</td>
          <td>${v.spo2}</td>
          <td>${v.bmi}</td>
          <td>${v.recordedBy}</td>
        </tr>`
      )
      .join("");

    const html = `
      <html>
        <head><title>Vitals Sheet - ${selectedPatient.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
          h1 { font-size: 20px; margin: 0 0 4px 0; }
          .meta { font-size: 13px; color: #6B7280; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th, td { border: 1px solid #E5E7EB; padding: 8px 10px; text-align: left; }
          th { background: #F9FAFB; font-weight: 600; color: #374151; }
        </style>
        </head>
        <body>
          <h1>Vitals Sheet — ${selectedPatient.name}</h1>
          <div class="meta">
            ${selectedPatient.opd || ""} • ${selectedPatient.age || ""} years • ${selectedPatient.gender || ""} • Printed on ${new Date().toLocaleString()}
          </div>
          <table>
            <thead><tr>
              <th>Date &amp; Time</th><th>BP (mmHg)</th><th>Pulse (bpm)</th><th>RR</th><th>Temp (°F)</th><th>SpO₂ (%)</th><th>BMI</th><th>Recorded By</th>
            </tr></thead>
            <tbody>${rows || `<tr><td colspan="8" style="text-align:center;color:#9CA3AF;">No vitals recorded yet</td></tr>`}</tbody>
          </table>
        </body>
      </html>`;

    const w = window.open("", "_blank", "width=900,height=650");
    if (!w) {
      Swal.fire({
        icon: "warning",
        title: "Popup blocked",
        text: "Please allow popups to print the vitals sheet.",
        confirmButtonColor: "#2F80ED",
      });
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const getGenderIcon = (gender) => {
    if (gender === "Male" || gender === "male")
      return <FaMars className={classes.maleIcon} />;
    if (gender === "Female" || gender === "female")
      return <FaVenus className={classes.femaleIcon} />;
    return null;
  };

  const renderAge = (age) => {
    if (age === null || age === undefined || age === "" || age === "—") {
      return "—";
    }
    return `${age} years`;
  };

  if (loading && allPatients.length === 0) {
    return (
      <div className={classes.page}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={classes.mainWrapper}>
          <Header toggleSidebar={toggleSidebar} />
          <div className={classes.contentArea}>
            <div className={classes.topSection}>
              <div className={classes.skeletonTitle}></div>
              <div className={classes.skeletonSubtitle}></div>
            </div>
            <div className={classes.mainGrid}>
              <div className={classes.leftPanel}>
                <div className={classes.skeletonQueueHeader}></div>
                <div className={classes.skeletonSearch}></div>
                <div className={classes.skeletonPatientList}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <SkeletonPatientCard key={i} />
                  ))}
                </div>
              </div>
              <div className={classes.rightPanel}>
                <div className={classes.skeletonSummaryHeader}></div>
                <div className={classes.skeletonSummaryList}>
                  {[1, 2, 3].map((i) => (
                    <SkeletonPatientCard key={i} />
                  ))}
                </div>
                <div className={classes.skeletonVitalsHeader}></div>
                <div className={classes.skeletonVitalsGrid}>
                  {[1, 2, 3, 4].map((i) => (
                    <SkeletonVitalSign key={i} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={classes.mainWrapper}>
        <Header toggleSidebar={toggleSidebar} />
        <div className={classes.contentArea}>
          <div className={classes.topSection}>
            <div>
              <h1 className={classes.pageTitle}>Nursing</h1>
              <p className={classes.breadcrumb}>
                Record vital signs, manage patient complaints and assign to doctor
              </p>
            </div>
          </div>

          <div className={classes.mainGrid}>
            {/* LEFT PANEL */}
            <div className={classes.leftPanel}>
              <div className={classes.queueHeader}>
                <h3>Patient Queue ({patients.length})</h3>
                <div className={classes.tabButtons}>
                  <button
                    className={`${classes.tabBtn} ${
                      activeTab === "queue" ? classes.activeTab : ""
                    }`}
                    onClick={() => setActiveTab("queue")}
                  >
                    All
                  </button>
                  <button
                    className={`${classes.tabBtn} ${
                      activeTab === "assigned" ? classes.activeTab : ""
                    }`}
                    onClick={() => setActiveTab("assigned")}
                  >
                    My Assigned
                  </button>
                </div>
              </div>

              <div className={classes.searchWrapper}>
                <FaSearch className={classes.searchIcon} />
                <input
                  type="text"
                  placeholder="Search patient by name, ID or phone..."
                  className={classes.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {error && (
                <div className={classes.queueError}>
                  <span>{error}</span>
                  <button onClick={fetchQueue}>Retry</button>
                </div>
              )}

              <div className={classes.patientList}>
                {loading ? (
                  [1, 2, 3, 4, 5].map((i) => <SkeletonPatientCard key={i} />)
                ) : patients.length > 0 ? (
                  patients.map((patient) => (
                    <div
                      key={patient.id}
                      className={`${classes.patientCard} ${
                        selectedPatient?.id === patient.id
                          ? classes.selectedPatient
                          : ""
                      }`}
                      onClick={() => handlePatientSelect(patient)}
                    >
                      <button
                        type="button"
                        className={classes.viewEvidenceBtn}
                        title="View payment evidence"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPatient(patient);
                          setEvidenceModalOpen(true);
                        }}
                      >
                        <FaEye />
                      </button>

                      <div className={classes.patientAvatar}>
                        <FaUser />
                      </div>
                      <div className={classes.patientInfo}>
                        <h4>{patient.name}</h4>
                        <p className={classes.patientStatus}>
                          {patient.status}
                        </p>
                        <p className={classes.patientOpd}>{patient.opd}</p>
                        <p className={classes.patientWaiting}>
                          <FaClock /> {patient.waitingTime}
                        </p>
                      </div>
                      <div className={classes.patientMeta}>
                        {getGenderIcon(patient.gender)}
                        <span className={classes.patientAge}>
                          {renderAge(patient.age)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={classes.noPatients}>
                    {activeTab === "assigned"
                      ? "No patients assigned yet"
                      : "No unassigned paid patients in queue"}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div className={classes.rightPanel}>
              {selectedPatient ? (
                <>
                  <div className={classes.summarySection}>
                    <h3>Patient Summary</h3>
                    <div className={classes.summaryCard}>
                      <div className={classes.summaryAvatar}>
                        <FaUser />
                      </div>
                      <div className={classes.summaryInfo}>
                        <h4>{selectedPatient.name}</h4>
                        <p className={classes.summaryStatus}>
                          {selectedPatient.status}
                        </p>
                        <p className={classes.summaryOpd}>
                          {selectedPatient.opd}
                        </p>
                        <p className={classes.summaryWaiting}>
                          <FaClock /> {selectedPatient.waitingTime}
                        </p>
                      </div>
                      <div className={classes.summaryMeta}>
                        {getGenderIcon(selectedPatient.gender)}
                        <span className={classes.summaryAge}>
                          {renderAge(selectedPatient.age)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ---------- ASSIGNED VIEW ---------- */}
                  {isAssignedView && (
                    <>
                      <div className={classes.assignedDoctorCard}>
                        <div className={classes.assignedDoctorHeader}>
                          <div className={classes.assignedDoctorIcon}>
                            <FaUserMd />
                          </div>
                          <div>
                            <h3>Assigned Doctor</h3>
                            <p>
                              This patient has been assigned &amp; scheduled.
                              Vital signs and doctor assignment are locked.
                            </p>
                          </div>
                          <span className={classes.lockBadge}>
                            <FaLock /> Read-only
                          </span>
                        </div>

                        {selectedPatient.assignedDoctor ? (
                          <div className={classes.assignedDoctorBody}>
                            <div className={classes.doctorBlock}>
                              <div className={classes.doctorAvatarLarge}>
                                {selectedPatient.assignedDoctor.avatar ? (
                                  <img
                                    src={selectedPatient.assignedDoctor.avatar}
                                    alt={selectedPatient.assignedDoctor.name}
                                  />
                                ) : (
                                  <FaUserMd />
                                )}
                              </div>
                              <div>
                                <h4>
                                  {selectedPatient.assignedDoctor.name || "—"}
                                </h4>
                                <p>
                                  {selectedPatient.assignedDoctor.department ||
                                    "—"}
                                  {selectedPatient.assignedDoctor
                                    .designation
                                    ? ` • ${selectedPatient.assignedDoctor.designation}`
                                    : ""}
                                </p>
                                <p className={classes.doctorPhone}>
                                  {selectedPatient.assignedDoctor.phone || "—"}
                                </p>
                              </div>
                            </div>

                            {selectedPatient.appointment && (
                              <div className={classes.appointmentInfo}>
                                <div>
                                  <span>Date</span>
                                  <strong>
                                    {selectedPatient.appointment.date}
                                  </strong>
                                </div>
                                <div>
                                  <span>Time</span>
                                  <strong>
                                    {selectedPatient.appointment.time}
                                  </strong>
                                </div>
                                <div>
                                  <span>Duration</span>
                                  <strong>
                                    {selectedPatient.appointment.duration} min
                                  </strong>
                                </div>
                                <div>
                                  <span>Type</span>
                                  <strong>
                                    {selectedPatient.appointment.type}
                                  </strong>
                                </div>
                                <div>
                                  <span>Status</span>
                                  <strong>
                                    {selectedPatient.appointment.status}
                                  </strong>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className={classes.assignedDoctorEmpty}>
                            No doctor details available for this consultation.
                          </p>
                        )}
                      </div>

                      <div className={classes.vitalsHistory}>
                        <div className={classes.vitalsHistoryHeader}>
                          <h3>Latest Vitals</h3>
                          <button
                            type="button"
                            className={classes.printVitalsBtn}
                            onClick={handlePrintVitals}
                            title="Print vitals sheet"
                          >
                            <FaPrint /> Print
                          </button>
                        </div>
                        <div className={classes.tableWrapper}>
                          <table className={classes.vitalsTable}>
                            <thead>
                              <tr>
                                <th>Date &amp; Time</th>
                                <th>BP (mmHg)</th>
                                <th>Pulse (bpm)</th>
                                <th>RR</th>
                                <th>Temp (°F)</th>
                                <th>SpO₂ (%)</th>
                                <th>BMI</th>
                                <th>Recorded By</th>
                              </tr>
                            </thead>
                            <tbody>
                              {isLoadingVitals ? (
                                <tr>
                                  <td
                                    colSpan="8"
                                    style={{
                                      textAlign: "center",
                                      color: "#9CA3AF",
                                      padding: "24px",
                                    }}
                                  >
                                    <FaSpinner className={classes.spin} />{" "}
                                    Loading vitals...
                                  </td>
                                </tr>
                              ) : latestVitals.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan="8"
                                    style={{
                                      textAlign: "center",
                                      color: "#9CA3AF",
                                      padding: "24px",
                                    }}
                                  >
                                    No vitals recorded yet
                                  </td>
                                </tr>
                              ) : (
                                latestVitals.map((vital, index) => (
                                  <tr key={vital.id ?? index}>
                                    <td>{vital.date}</td>
                                    <td>{vital.bp}</td>
                                    <td>{vital.pulse}</td>
                                    <td>{vital.rr}</td>
                                    <td>{vital.temp}</td>
                                    <td>{vital.spo2}</td>
                                    <td>{vital.bmi}</td>
                                    <td>{vital.recordedBy}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ---------- QUEUE VIEW ---------- */}
                  {!isAssignedView && (
                    <>
                      <div className={classes.vitalsSection}>
                        <h3>Vital Signs</h3>
                        <div className={classes.vitalsGrid}>
                          <div className={classes.vitalItem}>
                            <label>
                              <FaHeartbeat /> Blood Pressure (mmHg)
                            </label>
                            <input
                              type="text"
                              name="bloodPressure"
                              value={vitalSigns.bloodPressure}
                              onChange={handleVitalChange}
                              className={classes.vitalInput}
                              placeholder="e.g. 120/80"
                            />
                            <span className={classes.vitalSub}>
                              Format: 120/80
                            </span>
                          </div>

                          <div className={classes.vitalItem}>
                            <label>
                              <FaHeartbeat /> Pulse (bpm)
                            </label>
                            <input
                              type="text"
                              name="pulse"
                              value={vitalSigns.pulse}
                              onChange={handleVitalChange}
                              className={classes.vitalInput}
                              placeholder="e.g. 78"
                            />
                            <span className={classes.vitalSub}>
                              Beats per minute
                            </span>
                          </div>

                          <div className={classes.vitalItem}>
                            <label>
                              <FaHeartbeat /> Respiratory Rate
                            </label>
                            <input
                              type="text"
                              name="respiratoryRate"
                              value={vitalSigns.respiratoryRate}
                              onChange={handleVitalChange}
                              className={classes.vitalInput}
                              placeholder="e.g. 18"
                            />
                            <span className={classes.vitalSub}>
                              Breaths per minute
                            </span>
                          </div>

                          <div className={classes.vitalItem}>
                            <label>
                              <FaThermometerHalf /> Temperature (°F)
                            </label>
                            <input
                              type="text"
                              name="temperature"
                              value={vitalSigns.temperature}
                              onChange={handleVitalChange}
                              className={classes.vitalInput}
                              placeholder="e.g. 98.6"
                            />
                            <span className={classes.vitalSub}>
                              Degrees Fahrenheit
                            </span>
                          </div>

                          <div className={classes.vitalItem}>
                            <label>
                              <FaHeartbeat /> SpO₂ (%)
                            </label>
                            <input
                              type="text"
                              name="spo2"
                              value={vitalSigns.spo2}
                              onChange={handleVitalChange}
                              className={classes.vitalInput}
                              placeholder="e.g. 98"
                            />
                            <span className={classes.vitalSub}>
                              Oxygen saturation
                            </span>
                          </div>

                          <div className={classes.vitalItem}>
                            <label>
                              <FaRulerVertical /> Height (cm)
                            </label>
                            <input
                              type="text"
                              name="height"
                              value={vitalSigns.height}
                              onChange={handleVitalChange}
                              className={classes.vitalInput}
                              placeholder="e.g. 170"
                            />
                            <span className={classes.vitalSub}>
                              BMI:{" "}
                              {vitalSigns.bmi || (
                                <em
                                  style={{
                                    color: "#C4C4C4",
                                    fontStyle: "normal",
                                  }}
                                >
                                  auto-calculated
                                </em>
                              )}
                            </span>
                          </div>

                          <div className={classes.vitalItem}>
                            <label>
                              <FaWeight /> Weight (kg)
                            </label>
                            <input
                              type="text"
                              name="weight"
                              value={vitalSigns.weight}
                              onChange={handleVitalChange}
                              className={classes.vitalInput}
                              placeholder="e.g. 70"
                            />
                            <span className={classes.vitalSub}>
                              Kilograms
                            </span>
                          </div>

                          <div className={classes.vitalItem}>
                            <label>
                              <FaWeight /> Mode of Check
                            </label>
                            <select
                              name="modeOfCheck"
                              value={vitalSigns.modeOfCheck}
                              onChange={handleVitalChange}
                              className={classes.vitalSelect}
                            >
                              <option>Manual</option>
                              <option>Digital</option>
                              <option>Automatic</option>
                            </select>
                            <span className={classes.vitalSub}>
                              How vitals were captured
                            </span>
                          </div>
                        </div>

                        <div className={classes.vitalRemarks}>
                          <label>Remarks (Optional)</label>
                          <textarea
                            name="remarks"
                            value={vitalSigns.remarks}
                            onChange={handleVitalChange}
                            placeholder="Enter any additional notes..."
                            className={classes.remarksInput}
                            rows="2"
                          />
                        </div>

                        <div className={classes.vitalsActions}>
                          <button
                            className={classes.saveVitalsBtn}
                            onClick={handleSaveVitals}
                            disabled={isSavingVitals}
                          >
                            {isSavingVitals ? (
                              <>
                                <FaSpinner className={classes.spin} />{" "}
                                Saving...
                              </>
                            ) : (
                              <>
                                <FaCheckCircle /> Save Vitals
                              </>
                            )}
                          </button>
                          <button
                            className={classes.assignDoctorBtn}
                            onClick={() => setAssignModalOpen(true)}
                          >
                            <FaUserMd /> Assign to Doctor
                          </button>
                        </div>
                      </div>

                      <div className={classes.vitalsHistory}>
                        <div className={classes.vitalsHistoryHeader}>
                          <h3>Latest Vitals</h3>
                          <button
                            type="button"
                            className={classes.printVitalsBtn}
                            onClick={handlePrintVitals}
                            title="Print vitals sheet"
                          >
                            <FaPrint /> Print
                          </button>
                        </div>
                        <div className={classes.tableWrapper}>
                          <table className={classes.vitalsTable}>
                            <thead>
                              <tr>
                                <th>Date &amp; Time</th>
                                <th>BP (mmHg)</th>
                                <th>Pulse (bpm)</th>
                                <th>RR</th>
                                <th>Temp (°F)</th>
                                <th>SpO₂ (%)</th>
                                <th>BMI</th>
                                <th>Recorded By</th>
                              </tr>
                            </thead>
                            <tbody>
                              {isLoadingVitals ? (
                                <tr>
                                  <td
                                    colSpan="8"
                                    style={{
                                      textAlign: "center",
                                      color: "#9CA3AF",
                                      padding: "24px",
                                    }}
                                  >
                                    <FaSpinner className={classes.spin} />{" "}
                                    Loading vitals...
                                  </td>
                                </tr>
                              ) : latestVitals.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan="8"
                                    style={{
                                      textAlign: "center",
                                      color: "#9CA3AF",
                                      padding: "24px",
                                    }}
                                  >
                                    No vitals recorded yet
                                  </td>
                                </tr>
                              ) : (
                                latestVitals.map((vital, index) => (
                                  <tr key={vital.id ?? index}>
                                    <td>{vital.date}</td>
                                    <td>{vital.bp}</td>
                                    <td>{vital.pulse}</td>
                                    <td>{vital.rr}</td>
                                    <td>{vital.temp}</td>
                                    <td>{vital.spo2}</td>
                                    <td>{vital.bmi}</td>
                                    <td>{vital.recordedBy}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className={classes.noSelection}>
                  <FaUser className={classes.noSelectionIcon} />
                  <p>Select a patient to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <PaymentEvidenceModal
        open={evidenceModalOpen}
        onClose={() => setEvidenceModalOpen(false)}
        patient={selectedPatient}
      />

      <AssignDoctorModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        patient={selectedPatient}
        onAssigned={handleAssigned}
      />
    </div>
  );
};

export default Nursing;